import React, { useState, useRef, useEffect } from 'react';
import { Camera, Trash2, X, RefreshCw, Settings, Image as ImageIcon, ImageOff, AlertTriangle, CheckCircle, Eye, FileDown, HelpCircle, Layers, ShieldCheck, Users, History, FileText, Download } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import html2pdf from 'html2pdf.js';
import { useAuth } from './AuthContext';
import { supabase } from './supabaseClient';
import { CompanyAdminPanel, openInspectionPdf, openBulkInspectionPdf, buildInspectionCardHtml, getLatestFeedback } from './AdminPanel';
import SuperAdminPanel from './SuperAdminPanel';
import { DateRangePicker } from './DateRangePicker';
import { runWithConcurrency } from './utils/concurrencyPool';
import { escapeHtml, getDeviceType, getBrowserType, exportCSV, formatSerial, isIOS as isIOSDevice } from './utils/format';
import { downscaleForPdf } from './utils/pdfImage';
import { usePendingPdfShare } from './utils/pdfShare';
import { AUTO_PRINT_SCRIPT } from './utils/printAutoScript';
import { BATCH_CONCURRENCY, DEPARTMENT_OPTIONS, IMAGE_COMPRESSION, FEEDBACK_LABEL } from './constants';
import { isNewDeployment, isSafeToAutoReload, UPDATE_CHECK_INTERVAL_MS, UPDATE_CHECK_MIN_GAP_MS } from './utils/updateCheck';
import { measureImagesSharpness, isBlurryImage, blurryImageBadgeHtml, BLURRY_IMAGE_LABEL, BLURRY_IMAGE_NOTE, BLURRY_RETAKE_MESSAGE } from './utils/imageSharpness';
import { newSaveRef } from './utils/saveRef';
import pkg from '../package.json';

const APP_VERSION = `v${pkg.version}`;
// ビルド時のコミット（package.json の build で VERCEL_GIT_COMMIT_SHA から渡す）。
// /api/version が返す現在のデプロイのコミットと比べ、新しい版が出ていれば再読み込みを促す
const BUILD_COMMIT = process.env.REACT_APP_COMMIT_SHA || '';
const DEVICE_TYPE = getDeviceType();
const BROWSER_TYPE = getBrowserType();
const USER_AGENT = typeof navigator !== 'undefined' ? navigator.userAgent : '';

// ヘルプモーダルのロール切替タブ。superadmin・adminは開発・サポート時に自分より下位の
// ロールが実際に見ている文面を確認する必要があるため、自分向けヘルプに加えて他ロール
// 向けヘルプも閲覧できる。取得可否はサーバー側（/api/help）でも判定している
const HELP_ROLE_TABS = [
  { key: 'superadmin', label: '開発者' },
  { key: 'admin', label: '管理者' },
  { key: 'user', label: '一般ユーザー' },
];

// 自分のロールで閲覧できるタブだけを返す（superadminは全部、adminは開発者向けを除く2つ、
// それ以外は空＝タブ自体を表示しない）
const helpTabsForRole = (role) =>
  role === 'superadmin' ? HELP_ROLE_TABS
  : role === 'admin' ? HELP_ROLE_TABS.filter(t => t.key !== 'superadmin')
  : [];

// 配列リテラルを毎レンダー作り直すとHelpMarkdownのメモ化が効かないためモジュール定数にする
const REMARK_PLUGINS = [remarkGfm];

// react-markdown v8は内部でメモ化しておらず、レンダーの度にMarkdown全体を再パースして
// 数百〜数千のノードを作り直す。ヘルプモーダルはAppの巨大なstateツリーの中にあるため、
// 無関係なstate更新でも毎回その再パースが走ってしまう。contentが変わらない限り
// 再レンダーしないようメモ化する
const HelpMarkdown = React.memo(function HelpMarkdown({ content }) {
  return <ReactMarkdown remarkPlugins={REMARK_PLUGINS}>{content}</ReactMarkdown>;
});

// /api/help が本文を持つロール。これ以外（member等）は一般ユーザー向けヘルプに集約される
// ためサーバー側の対応に合わせて正規化する（先読みと本読みでキーが食い違わないように）
const helpRoleFor = (role) => (role === 'superadmin' || role === 'admin' ? role : 'user');

// ロール未指定（＝自分のロール向け）の取得を進行中Promiseの管理で識別するためのキー。
// 実際のロール名と衝突しない値にする
const OWN_HELP_KEY = '__own';

// ヘルプ本文を取得する。roleは「他ロール向けを明示指定して見る」場合のみ渡し、自分の
// ロール向けを見るときはnullを渡してサーバーに決めてもらう（profile未解決のまま推測で
// 指定すると、adminが一般ユーザー向けを明示指定したものとして通ってしまうため）。
// 閲覧可否はサーバー側でも判定している
async function fetchHelpContent(role, token) {
  const res = await fetch(role ? `/api/help?role=${encodeURIComponent(role)}` : '/api/help', {
    headers: { Authorization: `Bearer ${token}` },
  });
  // 200でもJSONでない応答（プロキシのエラーページ等）がありうる。ここで握り潰すと
  // 空文字が本文としてキャッシュされ、エラーも再取得もないまま白紙になる
  let data = null;
  try { data = await res.json(); } catch { /* 下でHTTPステータスとして扱う */ }
  if (!res.ok || data?.error) throw new Error(data?.error || `HTTP ${res.status}`);
  if (typeof data?.content !== 'string') throw new Error('ヘルプ本文を取得できませんでした');
  return { role: data.role || role, content: data.content };
}

export default function ProductMatcher() {
  const { profile, company, session, signOut: rawSignOut } = useAuth();
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [debugTiming, setDebugTiming] = useState(null);
  // ホーム画面に追加してスタンドアロン起動しているか（ブラウザタブ表示かの目安）。
  // iOS Safari/Chromeとも display-mode: standalone で判定可能（Chromeも iOS16.4+でmanifestの
  // display:standaloneを尊重するようになったため、Safari専用のnavigator.standaloneには頼らない）
  const [isStandalone] = useState(() => {
    try {
      return window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone === true;
    } catch { return false; }
  });

  const [items, setItems] = useState([]);
  const [analyzing, setAnalyzing] = useState(false);
  // 照合レポートPDFの生成・保存。iOSはタップ直後の一時的なユーザーアクティベーション
  // （約5秒）が切れると navigator.share が拒否される。生成がこの枠に収まれば
  // そのまま共有シートまで進み、間に合わなかった場合だけ pendingPdf に退避して
  // 「PDFを保存」ボタンのタップで共有する（usePendingPdfShareのshareOrDefer参照）
  const [exporting, setExporting] = useState(false);
  // exportAndClear の冒頭、未保存項目の自動再保存を待っている間だけtrueになる。
  // ボタンの表示切替（「保存を確認中...」→「生成中...」）に使う
  const [savingBeforeExport, setSavingBeforeExport] = useState(false);
  const { pendingPdf, setPendingPdf, shareOrDefer, sharePendingPdf } = usePendingPdfShare();
  // 生成済みPDFが残っている状態で検査が増減すると、PDFの内容と画面の照合結果が
  // 食い違う。「PDF出力 & クリア」を押し直したときに古いPDFを保存してしまわないよう
  // 破棄する（破棄後は押し直せば作り直される）
  const pdfItemIdsKey = items.map(i => i.id).join(',');
  useEffect(() => { setPendingPdf(null); }, [pdfItemIdsKey, setPendingPdf]);

  // 撮影セッション
  const [currentSessionImages, setCurrentSessionImages] = useState([]);
  const [showSessionModal, setShowSessionModal] = useState(false);

  const cameraInputRef = useRef(null);
  const galleryInputRef = useRef(null);
  const batchGalleryRef = useRef(null); // multiple専用（DOM属性固定）
  const inputModeRef = useRef('single');
  const lastCompressMsRef = useRef(null);

  // バッチ処理
  const [batchImages, setBatchImages] = useState([]); // [{file, base64}]
  const [showBatchModal, setShowBatchModal] = useState(false);
  const [batchProgress, setBatchProgress] = useState(null); // {current, total}
  const [batchResults, setBatchResults] = useState([]); // 完了済みアイテムの結果
  const [processingIndices, setProcessingIndices] = useState(new Set());
  const batchAbortRef = useRef(false);
  const [batchAborting, setBatchAborting] = useState(false);

  const deptLabel = (key) => (key ? (company?.department_labels?.[key] || key) : '');

  const DEFAULT_MODEL = 'gemini-3.5-flash-lite';
  const [modelName, setModelName] = useState(() => localStorage.getItem('pm_modelName') || DEFAULT_MODEL);
  const [availableModels, setAvailableModels] = useState([]);
  const [showSettings, setShowSettings] = useState(false);
  const [showHelp, setShowHelp] = useState(false);

  // 検査履歴（一般ユーザー向け）。管理者向けCompanyAdminPanel/SuperAdminPanelの検査履歴タブ
  // とは別の、ロール問わず全ユーザーが使える「自分自身の検査履歴」タブ。絞り込み範囲は
  // サーバー側（/api/history）で常に自分自身（user_email）に限定される
  const [showHistory, setShowHistory] = useState(false);
  const [historyItems, setHistoryItems] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [histDateFrom, setHistDateFrom] = useState('');
  const [histDateTo, setHistDateTo] = useState('');
  const [histProductName, setHistProductName] = useState('');
  const [histStatus, setHistStatus] = useState(['SAFE', 'DANGER', 'ERROR']);
  const [historyPdfBusy, setHistoryPdfBusy] = useState(false);
  // 検査履歴タブのPDF出力（個別・全件）用。メイン画面のexportAndClearとは別に持つ
  // （メイン側は保存完了と照合結果のクリアが連動しており、履歴タブのPDFに
  // それを適用すると無関係な照合結果（items）まで消えてしまうため）
  const { pendingPdf: historyPendingPdf, shareOrDefer: shareHistoryOrDefer, sharePendingPdf: shareHistoryPendingPdf } = usePendingPdfShare();
  const [helpContent, setHelpContent] = useState({});
  // ヘルプモーダルで現在表示しているロール（superadminのみ切替可能）
  const [helpRole, setHelpRole] = useState('user');
  const [helpError, setHelpError] = useState(null);
  // モーダルの枠を先に描画してから重いMarkdownをマウントするためのフラグ
  const [helpBodyReady, setHelpBodyReady] = useState(false);
  const helpScrollRef = useRef(null);
  // 取得済み本文のミラー。先読みeffectの依存にhelpContentを足さずに取得済み判定を行う
  const helpContentRef = useRef({});
  // 進行中のfetchをロール毎に保持し、クリック・タブ切替・先読みで共有する
  const helpRequestsRef = useRef({});
  const [loadingModels, setLoadingModels] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [lotNumber, setLotNumber] = useState('');
  const [feedbacks, setFeedbacks] = useState({});
  const [feedbackDraft, setFeedbackDraft] = useState({});
  const [speedMode, setSpeedMode] = useState(true);
  const [showAnalyzeTime, setShowAnalyzeTime] = useState(() => localStorage.getItem('pm_showAnalyzeTime') === 'true');
  useEffect(() => { localStorage.setItem('pm_showAnalyzeTime', showAnalyzeTime ? 'true' : 'false'); }, [showAnalyzeTime]);
  const [deployInfo, setDeployInfo] = useState(null);

  // 新しいバージョンのデプロイ検知（src/utils/updateCheck.js、food-label-assist と同じ実装）。
  // 開いたままの画面は読み込み直すまで古い版で動き続け、ホーム画面から起動したアプリには
  // 再読み込みの手段が無いため、新しい版を見つけたら帯と［再読み込み］ボタンを出す
  const [updateAvailable, setUpdateAvailable] = useState(false);
  // 前面に戻ったときに自動で読み込み直してよいかの材料。イベントハンドラから最新の
  // 状態を読むため ref に入れる（読み込み直すと消えるものが画面にあれば帯だけ出す）
  const screenStateRef = useRef(null);
  screenStateRef.current = {
    itemCount: items.length,
    sessionImageCount: currentSessionImages.length,
    batchImageCount: batchImages.length,
    busy: analyzing || exporting || savingBeforeExport || !!batchProgress || batchAborting
      || historyPdfBusy || historyLoading || loadingModels,
    overlayOpen: !!showAdminPanel || showSettings || showHistory || showHelp || !!editingItem
      || showSessionModal || showBatchModal || !!pendingPdf || !!historyPendingPdf,
  };
  // 最後に操作した時刻。撮影・写真選択の画面から戻った直後（写真の圧縮中で画面が空に
  // 見える）等、画面の状態だけでは分からない作業中を、操作の直後かどうかで除外する
  const lastInteractionAtRef = useRef(Date.now());
  useEffect(() => {
    const mark = () => { lastInteractionAtRef.current = Date.now(); };
    const events = ['pointerdown', 'keydown', 'change'];
    events.forEach(e => document.addEventListener(e, mark, true));
    return () => events.forEach(e => document.removeEventListener(e, mark, true));
  }, []);

  const hasSession = !!session?.access_token;
  useEffect(() => {
    // /api/versionは認証必須（コミットSHA/メッセージ/環境名の非公開化）。
    // ログイン完了後に取得し、以後は前面に戻ったとき・一定時間ごとに取得し直す
    if (!hasSession) { setDeployInfo(null); return; }
    let lastCheckedAt = 0;
    let inFlight = false;
    const checkForUpdate = async ({ fromForeground = false } = {}) => {
      if (inFlight || Date.now() - lastCheckedAt < UPDATE_CHECK_MIN_GAP_MS) return;
      inFlight = true;
      try {
        // トークンは1時間で更新されるため、呼ぶたびに最新のセッションから取り出す。
        // iOSのホーム画面アプリは復帰直後に getSession() が null を返しうるため、
        // 一度だけリフレッシュを試す
        let { data: { session: s } } = await supabase.auth.getSession();
        if (!s?.access_token) ({ data: { session: s } } = await supabase.auth.refreshSession());
        if (!s?.access_token) return;
        const r = await fetch('/api/version', { headers: { Authorization: `Bearer ${s.access_token}` }, cache: 'no-store' });
        if (!r.ok) return;
        const data = await r.json();
        // 確認できたときだけ最小間隔を数え始める（復帰直後の通信失敗で、次の確認が
        // 30分後まで遅れないようにするため）
        lastCheckedAt = Date.now();
        setDeployInfo(prev => prev || data);
        if (isNewDeployment(BUILD_COMMIT, data?.commit)) {
          const safe = isSafeToAutoReload({
            ...screenStateRef.current,
            msSinceInteraction: Date.now() - lastInteractionAtRef.current,
          });
          if (fromForeground && safe) {
            window.location.reload();
            return;
          }
          setUpdateAvailable(true);
        }
      } catch (err) {
        // 通信できない場合は次の機会に確認する（検査の操作は妨げない）
        console.warn('[version]', err?.message || err);
      } finally {
        inFlight = false;
      }
    };
    checkForUpdate();
    const onVisible = () => {
      if (document.visibilityState === 'visible') checkForUpdate({ fromForeground: true });
    };
    document.addEventListener('visibilitychange', onVisible);
    // 定期確認は表に出ている間だけ（裏のタブで一日中呼び続けない）
    const timer = setInterval(() => { if (!document.hidden) checkForUpdate(); }, UPDATE_CHECK_INTERVAL_MS);
    return () => {
      document.removeEventListener('visibilitychange', onVisible);
      clearInterval(timer);
    };
  }, [hasSession]);

  // 店舗情報（Supabase DB から選択）
  const [storeName, setStoreName] = useState(() => localStorage.getItem('pm_storeName') || '');
  const [deptName,  setDeptName]  = useState(() => { const v = localStorage.getItem('pm_deptName'); return DEPARTMENT_OPTIONS.includes(v) ? v : ''; });

  useEffect(() => { localStorage.setItem('pm_storeName', storeName); }, [storeName]);
  useEffect(() => { localStorage.setItem('pm_deptName',  deptName);  }, [deptName]);
  useEffect(() => { localStorage.setItem('pm_modelName', modelName); }, [modelName]);

  const signOut = async () => {
    setStoreName('');
    setDeptName('');
    setModelName('gemini-3.5-flash-lite');
    await rawSignOut();
  };

  // 画像圧縮ロジック
  // reader/imgともにonerrorを配線していないと、壊れた/非対応形式のファイルで
  // Promiseが永久に解決も拒否もされず、呼び出し元（Promise.all）がハングする
  const compressImage = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onerror = () => reject(new Error(`ファイルの読み込みに失敗しました: ${file.name}`));
      reader.onload = (event) => {
        const img = new Image();
        img.onerror = () => reject(new Error(`画像として読み込めませんでした: ${file.name}`));
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;
          const { MAX_SIZE, QUALITY: quality } = speedMode ? IMAGE_COMPRESSION.SPEED : IMAGE_COMPRESSION.ACCURACY;
          if (width > height) {
            if (width > MAX_SIZE) { height *= MAX_SIZE / width; width = MAX_SIZE; }
          } else {
            if (height > MAX_SIZE) { width *= MAX_SIZE / height; height = MAX_SIZE; }
          }
          canvas.width = width;
          canvas.height = height;
          canvas.getContext('2d').drawImage(img, 0, 0, width, height);
          resolve(canvas.toDataURL('image/jpeg', quality));
        };
      };
    });
  };

  // 指定ロール向けヘルプを取得してキャッシュする。同じロールの取得が進行中ならその
  // Promiseを共有するため、先読み中にクリックされても二重取得にならず、失敗も
  // 呼び出し側に伝わる（呼び出し側は待たずにモーダルを開き、未取得の間は本文に
  // 「読み込み中」を表示する）。finallyで消すので失敗後のクリックは再取得になる
  // roleがnullの場合は「自分のロール向け（サーバーが決める）」を意味する。この場合だけは
  // 取得完了まで対象ロールが分からないため、進行中Promiseの管理にOWN_HELP_KEYを使う
  const loadHelp = (role, token) => {
    const key = role || OWN_HELP_KEY;
    if (role && helpContentRef.current[role] !== undefined) return Promise.resolve();
    if (!helpRequestsRef.current[key]) {
      helpRequestsRef.current[key] = fetchHelpContent(role, token)
        .then(({ role: resolved, content }) => {
          // refとstateに同じオブジェクトを入れ、両者が食い違わないようにする
          helpContentRef.current = { ...helpContentRef.current, [resolved]: content };
          setHelpContent(helpContentRef.current);
          // ロール未指定で取得した場合と、サーバー側が権限に応じて読み替えた場合は
          // 表示中のタブも合わせる
          if (resolved !== role) setHelpRole(cur => (role === null || cur === role ? resolved : cur));
        })
        .finally(() => { delete helpRequestsRef.current[key]; });
    }
    return helpRequestsRef.current[key];
  };

  const showHelpFetchError = (error) => setHelpError('ヘルプの取得に失敗しました: ' + error.message);

  // 取得を待たずに即座に開く（待ち時間中は本文に「読み込み中」を表示）。
  // profileはsessionとは別に非同期取得されるため（src/index.jsのゲートはsessionのみ）、
  // 解決前にヘルプを開かれることがある。その状態でロールを推測して?role=を送ると、
  // adminが一般ユーザー向けを明示指定したものとして通ってしまうため、ロールが
  // 分からないうちは指定せずサーバーに決めてもらう
  const openHelp = () => {
    const role = profile ? helpRoleFor(profile.role) : null;
    if (role) setHelpRole(role);
    setHelpError(null);
    setShowHelp(true);
    loadHelp(role, session?.access_token).catch(showHelpFetchError);
  };

  // 取得を待たずに即座にタブを切り替える
  const switchHelpRole = (role) => {
    setHelpRole(role);
    setHelpError(null);
    if (helpScrollRef.current) helpScrollRef.current.scrollTop = 0;
    loadHelp(role, session?.access_token).catch(showHelpFetchError);
  };

  // ヘルプを開いた直後は枠だけを描画し、次フレームでMarkdownをマウントする。
  // 大きな資料では数千ノードのレンダーで数百ms以上かかるため、先に枠を出して
  // 「押しても何も起きない」状態をなくす
  useEffect(() => {
    if (!showHelp) { setHelpBodyReady(false); return; }
    const id = requestAnimationFrame(() => setHelpBodyReady(true));
    return () => cancelAnimationFrame(id);
  }, [showHelp]);

  // 初回クリック時のネットワーク待ちをなくすため、アイドル時に自分のロール向けヘルプを
  // 先読みしておく。データセーバー有効時は先読みせずクリック時の取得に任せる
  // AuthContextは認証イベント毎にsession・profileを作り直す（TOKEN_REFRESHEDは定期的に
  // 発火する）ため、このeffectは繰り返し再実行される。取得済みなら何もしないことで
  // 本文の取り直しを防ぐ
  useEffect(() => {
    const token = session?.access_token;
    const role = helpRoleFor(profile?.role);
    if (!token || !profile) return;
    if (navigator.connection?.saveData) return;
    if (helpContentRef.current[role] !== undefined) return;
    // window.requestIdleCallbackを変数に代入して呼ぶとChromeでIllegal invocationになるため、
    // 常にwindowのメソッドとして呼ぶ（未対応ブラウザではsetTimeoutで代替）
    const idle = cb => (window.requestIdleCallback ? window.requestIdleCallback(cb) : setTimeout(cb, 2000));
    const cancel = id => (window.requestIdleCallback ? window.cancelIdleCallback(id) : clearTimeout(id));
    const id = idle(() => {
      loadHelp(role, token).catch(err => console.warn('[help prefetch]', err.message));
    });
    return () => cancel(id);
  }, [profile, session]);

  // =============================================
  // 検査履歴（一般ユーザー向け、自分自身の検査のみ）
  // =============================================
  const fetchHistory = async () => {
    setHistoryLoading(true);
    try {
      const params = new URLSearchParams();
      if (histDateFrom) params.set('dateFrom', histDateFrom);
      if (histDateTo) params.set('dateTo', histDateTo);
      if (histProductName.trim()) params.set('productName', histProductName.trim());
      if (histStatus.length && histStatus.length < 3) params.set('status', histStatus.join(','));
      const res = await fetch(`/api/history?${params.toString()}`, { headers: { Authorization: `Bearer ${session?.access_token}` } });
      const data = await res.json();
      if (!res.ok || data.error) throw new Error(data.error || `HTTP ${res.status}`);
      setHistoryItems(data.inspections || []);
      setHistoryLoaded(true);
    } catch (error) {
      console.error('[fetchHistory]', error);
      alert('検査履歴の取得に失敗しました: ' + error.message);
    } finally {
      setHistoryLoading(false);
    }
  };

  const openHistoryTab = () => {
    setShowHistory(true);
    if (!historyLoaded) fetchHistory();
  };

  const handleHistoryItemPdf = async (insp) => {
    setHistoryPdfBusy(true);
    try {
      await openInspectionPdf(insp, {
        exporterEmail: session?.user?.email || '―',
        onIOSPdfReady: shareHistoryOrDefer,
      });
    } finally {
      setHistoryPdfBusy(false);
    }
  };

  const handleHistoryBulkPdf = async () => {
    if (historyItems.length === 0) { alert('出力可能な検査結果がありません'); return; }
    setHistoryPdfBusy(true);
    try {
      await openBulkInspectionPdf(historyItems, {
        exporterEmail: session?.user?.email || '―',
        onIOSPdfReady: shareHistoryOrDefer,
      });
    } finally {
      setHistoryPdfBusy(false);
    }
  };

  // 列構成はsrc/AdminPanel.jsのexportInspHistoryCSV（企業Admin向け）と完全に一致させる
  // こと。使用モデル・端末・ブラウザ・動作モードは開発者向けデータのため含めない
  const exportHistoryCSV = () => {
    if (historyItems.length === 0) { alert('出力可能な検査結果がありません'); return; }
    const headers = ['シリアル', '店舗', '部門', 'ロット番号', '商品名(ラベル)', '目視識別', '判定', '判定理由', '検査日時', '担当者メール', '検査モード', 'フィードバック', 'FBコメント', '判定時間(秒)'];
    const rows = historyItems.map(r => {
      const fb = getLatestFeedback(r.inspection_feedbacks);
      return [
        formatSerial(r.serial), r.store_name ?? '', r.dept_name ?? '', r.lot_number ?? '',
        r.label_product_name ?? '', r.detected_food_visual ?? '', r.match_status ?? '',
        r.reason ?? '',
        r.inspected_at ? new Date(r.inspected_at).toLocaleString('ja-JP') : '',
        r.user_email ?? '',
        r.image_mode === 'speed' ? '速度優先' : r.image_mode === 'accuracy' ? '精度優先' : '',
        fb ? FEEDBACK_LABEL[fb.verdict] ?? fb.verdict : '',
        fb?.comment ?? '',
        r.analyze_ms != null ? (r.analyze_ms / 1000).toFixed(2) : '',
      ];
    });
    const date = new Date().toISOString().slice(0, 10);
    exportCSV(headers, rows, `PM_検査履歴_自分の検査_${date}.csv`);
  };

  // モデル一覧取得
  const fetchModels = async () => {
    setLoadingModels(true);
    try {
      const devApiKey = process.env.REACT_APP_GEMINI_API_KEY;
      const response = devApiKey
        ? await fetch(`https://generativelanguage.googleapis.com/v1beta/models?key=${devApiKey}`)
        : await fetch('/api/models', { headers: { 'Authorization': `Bearer ${session?.access_token}` } });
      const data = await response.json();
      if (data.error) throw new Error(data.error.message || JSON.stringify(data.error));

      const raw = data.models || [];
      const models = devApiKey
        ? raw.filter(m => m.supportedGenerationMethods?.includes('generateContent')).map(m => m.name.replace('models/', ''))
        : raw;
      setAvailableModels(models);

      const flash = models.find(m => m.includes("flash") && !m.includes("lite"));
      const lite = models.find(m => m.includes("flash-lite"));
      const bestModel = flash || lite || models[0];
      if (bestModel && !models.includes(modelName)) {
        setModelName(bestModel);
      }
      alert(`利用可能なモデルを ${models.length} 件取得しました。`);
    } catch (error) {
      alert("モデル一覧の取得に失敗しました。\n" + error.message);
    } finally {
      setLoadingModels(false);
    }
  };

  // 検査結果をDBに保存（シリアル番号を返すPromise）
  // セッションが取れない場合は一度だけリフレッシュを試してから諦める。
  // iOSのホーム画面起動（manifest.jsonはdisplay:standalone）ではアプリが頻繁に
  // サスペンドされ、復帰直後は自動リフレッシュが走る前でgetSession()がnullを返しうる。
  // 姉妹アプリfood-label-assistで見つかった不具合と同じ対策（PR#138参照）
  const getAccessToken = async () => {
    const { data: { session: s } } = await supabase.auth.getSession();
    if (s?.access_token) return s.access_token;
    try {
      const { data: { session: r } } = await supabase.auth.refreshSession();
      return r?.access_token || null;
    } catch (err) {
      console.warn('[getAccessToken] refreshSession failed:', err?.message || err);
      return null;
    }
  };

  // /api/inspect-save への送信部分。単発（saveItem）・バッチ（processBatch）・
  // 未保存カードの「再保存」（retrySave）から共用する。戻り値で成否と理由を返し、
  // 呼び出し側が項目に印を付ける。
  //
  // 以前は成否を一切判定せず、fetchが投げた例外だけ握り潰して{}を返し、呼び出し側は
  // data?.serial || data?.id の有無だけで判断していた。トークン未取得・サーバーエラー・
  // 通信例外のいずれも同じ「serialが付かない」結果にしかならず、画面上は保存されたように
  // 見えるのに検査番号が付かない不具合の原因になっていた
  // （姉妹アプリfood-label-assistで見つかった不具合と同じ構造。PR#138参照）
  const postInspection = async (item, lotNum) => {
    const token = await getAccessToken();
    if (!token) {
      console.warn('[postInspection] no supabase session');
      return { ok: false, reason: 'セッションが切れています。再ログインしてください' };
    }
    try {
      const res = await fetch('/api/inspect-save', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          store_name: storeName || null,
          dept_name: deptName || null,
          lot_number: lotNum || null,
          label_product_name: item.label_product_name || null,
          detected_food_visual: item.detected_food_visual || null,
          match_status: item.match_status || null,
          reason: item.reason || null,
          ingredients_check: item.ingredients_check || null,
          model_used: item.modelUsed || null,
          analyze_ms: item.analyzeMs || null,
          app_version: APP_VERSION,
          image_mode: item.imageMode || null,
          device_type: DEVICE_TYPE,
          user_agent: USER_AGENT,
          browser_type: BROWSER_TYPE,
          is_standalone: isStandalone,
          images: item.images || [],
          image_quality: item.image_quality || null,
          client_ref: item.saveRef || null,
        }),
      });
      let data;
      try { data = await res.json(); } catch (_) {
        console.error('[postInspection] non-JSON response (status=' + res.status + ')');
        return { ok: false, reason: `サーバーが応答しませんでした（HTTP ${res.status}）` };
      }
      if (!data.success) {
        console.error('[postInspection] save failed:', data.error || data);
        return { ok: false, reason: data.error || `保存に失敗しました（HTTP ${res.status}）` };
      }
      // 保存自体は成功したが検査番号が採番されていないケース。api/inspect-save.js は
      // profile.company_id が無いと company_id:null で登録し、採番トリガーが
      // 素通りするため起きる（company_idがある場合はサーバー側で500エラーになる）。
      // DB行は存在するので「未保存」とは区別して伝える
      if (data.serial == null) {
        console.warn('[postInspection] saved without serial (company_id missing?)');
        return { ok: true, data, warn: '検査番号が採番されませんでした（所属企業が未設定の可能性があります）' };
      }
      return { ok: true, data };
    } catch (err) {
      console.error('[postInspection]', err);
      return { ok: false, reason: '通信に失敗しました。電波状況を確認してください' };
    }
  };

  // postInspection の結果を項目に反映する。失敗・警告はsaveErrorに残し、
  // カード表示（未保存バッジ）とPDF出力・クリア前の確認で参照する
  const applySaveResult = (itemId, result) => {
    setItems(prev => prev.map(i => {
      if (i.id !== itemId) return i;
      if (!result.ok) return { ...i, saving: false, saveError: result.reason };
      return { ...i, saving: false, dbId: result.data.id, serial: result.data.serial, saveError: result.warn || null };
    }));
  };

  const analyzeImages = async (base64Images) => {
    setAnalyzing(true);
    try {
      // ピントの鋭さの計測（「画像不鮮明」の判断に使う）。失敗しても解析は止めない。
      // 判定時間（analyze_ms）に計測の時間を含めないよう、計測の後から測る（一括撮影と同じ）
      const imageSharpness = await measureImagesSharpness(base64Images);
      const startTime = Date.now();
      const response = await fetch('/api/analyze', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
        body: JSON.stringify({ base64Images, imageSharpness, modelName, companyId: company?.id || null })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);

      const analyzeMs = Date.now() - startTime;
      setLotNumber('');
      setEditingItem({ id: Date.now(), images: base64Images, lotNumber: '', analyzeMs, modelUsed: modelName, imageMode: speedMode ? 'speed' : 'accuracy', ...data });
      setCurrentSessionImages([]);
      setShowSessionModal(false);

    } catch (error) {
      alert("解析エラー: " + error.message);
    } finally {
      setAnalyzing(false);
    }
  };

  const handleBatchSelect = async (event) => {
    const files = Array.from(event.target.files);
    if (files.length === 0) { event.target.value = ''; return; }
    const compressStart = Date.now();
    // Promise.allだと1件でも失敗（壊れたファイル等）すると全体がrejectされ、
    // 他の正常な画像まで巻き添えでバッチに追加されなくなる。allSettledで
    // 個別に結果を見て、失敗したファイルだけスキップする
    const settled = await Promise.allSettled(files.map(f => compressImage(f)));
    console.log(`[Timing] Batch compress (${files.length} files): ${Date.now() - compressStart}ms`);
    const succeeded = [];
    const failedNames = [];
    settled.forEach((result, i) => {
      if (result.status === 'fulfilled') {
        succeeded.push({ name: files[i].name, base64: result.value });
      } else {
        console.error('[compressImage]', files[i].name, result.reason?.message);
        failedNames.push(files[i].name);
      }
    });
    if (failedNames.length > 0) {
      alert(`以下のファイルを読み込めなかったためスキップしました:\n${failedNames.join('\n')}`);
    }
    setBatchImages(prev => [...prev, ...succeeded]);
    if (!showBatchModal) setShowBatchModal(true);
    event.target.value = '';
  };

  const processBatch = async () => {
    if (batchImages.length === 0) return;
    batchAbortRef.current = false;
    setBatchAborting(false);
    setAnalyzing(true);
    setBatchProgress({ current: 0, total: batchImages.length });
    setBatchResults(new Array(batchImages.length).fill(null));
    setProcessingIndices(new Set());

    const pooled = await runWithConcurrency(batchImages, async (imgEntry, idx) => {
      setProcessingIndices(prev => new Set(prev).add(idx));
      try {
        const { base64 } = imgEntry;
        const imageSharpness = await measureImagesSharpness([base64]);
        const startTime = Date.now();
        const response = await fetch('/api/analyze', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${session?.access_token}` },
          body: JSON.stringify({ base64Images: [base64], imageSharpness, modelName, companyId: company?.id || null })
        });
        const data = await response.json();
        if (data.error) throw new Error(data.error);
        const resultItem = { id: Date.now() + idx, images: [base64], lotNumber: '', analyzeMs: Date.now() - startTime, modelUsed: modelName, imageMode: speedMode ? 'speed' : 'accuracy', ...data, saveRef: newSaveRef() };
        const result = await postInspection(resultItem, '');
        if (result.ok) {
          resultItem.serial = result.data.serial;
          resultItem.dbId = result.data.id;
          if (result.warn) resultItem.saveError = result.warn;
        } else {
          resultItem.saveError = result.reason;
        }
        return resultItem;
      } finally {
        setProcessingIndices(prev => { const next = new Set(prev); next.delete(idx); return next; });
      }
    }, {
      concurrency: BATCH_CONCURRENCY,
      isAborted: () => batchAbortRef.current,
      onSettle: ({ index, value, status, completed, total }) => {
        setBatchProgress({ current: completed, total });
        const item = status === 'fulfilled' ? value : { id: Date.now() + index, images: [batchImages[index].base64], lotNumber: '', match_status: 'ERROR', reason: String(value?.message || value || 'エラー'), label_product_name: batchImages[index].name };
        setBatchResults(prev => { const next = [...prev]; next[index] = item; return next; });
      },
    });

    // 失敗した項目（match_status: 'ERROR'）も、成功した項目（ワーカー内で既に
    // postInspection済み）と同様にDBへ保存する。以前はここでERROR項目を
    // クライアント側のitems表示用に作るだけでDBには一切保存しておらず、
    // AdminPanel/SuperAdminPanelの「エラー」件数表示が常に0件のまま機能していなかった
    const results = await Promise.all(
      pooled
        .filter(r => r.status !== 'skipped')
        .map(async (r) => {
          if (r.status === 'fulfilled') return r.value;
          const errorItem = { id: Date.now() + r.index, images: [batchImages[r.index].base64], lotNumber: '', match_status: 'ERROR', reason: String(r.error?.message || r.error), label_product_name: batchImages[r.index].name, saveRef: newSaveRef() };
          const result = await postInspection(errorItem, '');
          if (result.ok) {
            errorItem.serial = result.data.serial;
            errorItem.dbId = result.data.id;
            if (result.warn) errorItem.saveError = result.warn;
          } else {
            errorItem.saveError = result.reason;
          }
          return errorItem;
        })
    );

    if (results.length > 0) setItems(prev => [...prev, ...results]);
    batchAbortRef.current = false;
    setBatchAborting(false);
    setShowBatchModal(false);
    setBatchImages([]);
    setBatchProgress(null);
    setBatchResults([]);
    setProcessingIndices(new Set());
    setAnalyzing(false);

    // 使用量をバッチ件数分カウント（エラー分は除く）
  };

  const handleImageSelect = async (event) => {
    const file = event.target.files[0];
    if (file) {
      try {
        const compressStart = Date.now();
        const compressed = await compressImage(file);
        lastCompressMsRef.current = Date.now() - compressStart;

        if (currentSessionImages.length >= 2) {
          alert("画像は最大2枚までです。");
          event.target.value = '';
          return;
        }

        const newImages = [...currentSessionImages, compressed];
        setCurrentSessionImages(newImages);
        setShowSessionModal(true);
      } catch (err) {
        alert("画像処理エラー");
      }
      event.target.value = '';
    }
  };

  const saveItem = async (item) => {
    // saveRef は再保存・自動の再試行でも同じ値を送る（同じ検査を 2 件にしない、src/utils/saveRef.js）
    const newItem = { ...item, lotNumber, saveRef: item.saveRef || newSaveRef() };
    setItems(prev => [...prev, newItem]);
    setEditingItem(null);
    setLotNumber('');
    const result = await postInspection(newItem, lotNumber);
    applySaveResult(item.id, result);
    if (result.ok) {
      const timing = {
        compress_ms: lastCompressMsRef.current ?? null,
        analyze_ms: item.analyzeMs ?? null,
        upload_ms: result.data?.timing?.upload_ms ?? null,
        save_ms: result.data?.timing?.save_ms ?? null,
      };
      console.log('[Inspection Timing]', timing);
      setDebugTiming({ mode: 'single', items: [timing] });
    }
  };

  // 実際に送信し、state（saveError・dbId・serial）へ反映したうえで、更新後の項目
  // オブジェクトも返す。React stateの更新は次の再レンダリングまで反映されないため、
  // 同じ関数内で連続して最新の保存結果を必要とする場合（ensureAllSavedの再試行ループ）
  // は、このオブジェクトをローカルに保持して使う
  const trySaveItem = async (item) => {
    setItems(prev => prev.map(i => i.id === item.id ? { ...i, saving: true, saveError: null } : i));
    const result = await postInspection(item, item.lotNumber);
    applySaveResult(item.id, result);
    if (!result.ok) return { ...item, saving: false, saveError: result.reason };
    return { ...item, saving: false, dbId: result.data.id, serial: result.data.serial, saveError: result.warn || null };
  };

  // 未保存カードの「再保存」。項目（画像のbase64を含む）はstateに残っているので
  // そのまま送り直せる。exportAndClear でクリアすると復旧できなくなるため、
  // クリア前にここで救済できるようにしている
  const retrySave = async (item) => {
    await trySaveItem(item);
  };

  const SAVE_RETRY_ATTEMPTS = 3;
  const SAVE_RETRY_DELAY_MS = 2000;

  // 保存できていない項目（DB行が無い＝クリアすると失われる項目）を、間隔を空けて
  // 自動的に再送信する。「保存できていませんが続けますか」という選択を利用者に
  // 委ねるとデータを失う判断を利用者にさせてしまうため、保存できるまで待つ設計にする。
  // トークン切れ等の一時的な失敗はgetAccessTokenのrefreshSessionやこの再試行で
  // 大抵は解消する。それでも残る場合は「所属企業が未設定で番号だけ付かない」ような
  // 構造的な問題ではなく、DB行自体が作られていない＝本当に保存されていない状態のため、
  // 呼び出し側で利用者に伝えて処理を中断する
  const ensureAllSaved = async (startItems) => {
    let latest = startItems;
    let pending = latest.filter(i => !i.dbId);
    for (let attempt = 0; attempt < SAVE_RETRY_ATTEMPTS && pending.length > 0; attempt++) {
      if (attempt > 0) await new Promise(r => setTimeout(r, SAVE_RETRY_DELAY_MS));
      const retried = await Promise.all(pending.map(trySaveItem));
      const byId = new Map(retried.map(i => [i.id, i]));
      latest = latest.map(i => byId.get(i.id) || i);
      pending = latest.filter(i => !i.dbId);
    }
    return { items: latest, stillUnsaved: pending };
  };

  const submitFeedback = async (item, verdict, comment) => {
    try {
      const res = await fetch('/api/feedback', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          inspection_id: item.dbId,
          verdict,
          comment: comment || null,
        }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.error || 'フィードバックの送信に失敗しました');
        return;
      }
      setFeedbacks(prev => ({ ...prev, [item.id]: verdict }));
      setFeedbackDraft(prev => { const n = { ...prev }; delete n[item.id]; return n; });
    } catch (err) { console.error('[feedback]', err.message); }
  };

  const getRiskColor = (status) => {
    return status === 'DANGER'
      ? 'bg-red-100 text-red-800 border-red-200'
      : 'bg-blue-100 text-blue-800 border-blue-200';
  };

  const esc = escapeHtml;

  // 保存できなかった（検査番号が付いていない）件数。クリア前の確認とバナーに使う
  const unsavedCount = items.filter(i => i.saveError && !i.dbId).length;

  // 別ウィンドウHTMLを生成して印刷（Android対応）。iOSはhtml2pdf.jsでPDF化して
  // 共有シートから保存する（下のisIOS分岐参照）
  const exportAndClear = async () => {
    if (items.length === 0 || exporting) return;

    // 前回のタップで生成は済んだがユーザーアクティベーションが切れて共有シートを
    // 開けなかった場合。作り直さずそのまま共有する（生成を挟まないので必ず枠内に
    // 収まる）。ボタンは「PDF出力 & クリア」のまま、押し直せば保存できる
    if (pendingPdf) {
      const saved = await sharePendingPdf();
      if (saved) setItems([]);
      return;
    }

    setExporting(true);
    // stateを立てただけでは、直後に重い処理がメインスレッドを占有するため
    // 「生成中...」が1フレームも描画されない。明示的に描画を1フレーム譲る
    await new Promise(r => requestAnimationFrame(() => setTimeout(r, 0)));
    try {
      // クリアすると照合結果は復旧できない（画像はメモリ上のbase64のみでDB行も無い）。
      // 保存できていない項目があれば、利用者に選ばせるのではなく保存できるまで
      // 自動で待つ（ensureAllSavedのコメント参照）
      setSavingBeforeExport(true);
      const { items: settledItems, stillUnsaved } = await ensureAllSaved(items);
      setSavingBeforeExport(false);
      if (stillUnsaved.length > 0) {
        alert(
          `${stillUnsaved.length}件がまだ保存できていません。通信状況をご確認のうえ、\n` +
          'しばらくしてから「PDF出力 & クリア」をもう一度お試しください\n' +
          '（各カードの「再保存」でも送り直せます）。\n' +
          'PDFの出力は行っていません。検査結果は消えていません。'
        );
        return;
      }
      await buildAndDeliverPdf(settledItems);
    } catch (err) {
      console.error('[exportAndClear]', err);
      alert('PDFの生成に失敗しました。件数を減らして再度お試しください。');
    } finally {
      setSavingBeforeExport(false);
      setExporting(false);
    }
  };

  // 照合レポートの組み立てと引き渡し。例外は呼び出し側（exportAndClear）で拾う。
  // itemsToExport は ensureAllSaved で保存確認済みの最新の項目一覧
  // （画面のitems stateはこの時点で再レンダリングが済んでいない可能性があるため、
  // 保存結果を確実に反映させるためにローカルの値を明示的に受け取る）
  const buildAndDeliverPdf = async (itemsToExport) => {
    const now = new Date();
    const pad = (n) => String(n).padStart(2, '0');
    const dateStr = `${now.getFullYear()}/${pad(now.getMonth()+1)}/${pad(now.getDate())} ${pad(now.getHours())}:${pad(now.getMinutes())}`;
    const fileDate = `${now.getFullYear()}${pad(now.getMonth()+1)}${pad(now.getDate())}_${pad(now.getHours())}${pad(now.getMinutes())}${pad(now.getSeconds())}`;
    const storePart = storeName ? `_${esc(storeName)}` : '';
    const deptPart  = deptName  ? `_${esc(deptLabel(deptName))}`  : '';
    const filename  = `PM_照合レポート${storePart}${deptPart}_${fileDate}`;

    const safeCount  = itemsToExport.filter(i => i.match_status === 'SAFE').length;
    const dangerCount = itemsToExport.filter(i => i.match_status === 'DANGER').length;
    // iOS経路の html2pdf.js は渡したHTML文字列をサニタイズして<div>へ流し込むため、
    // <style>要素ごと落ちる（.from(html,'string')の生成物の計算済みスタイルで確認済み：
    // table-layout→auto、border-collapse→separate、th の背景・文字色・罫線→すべて既定値）。
    // 生き残るのはインラインの style 属性だけなので、レイアウトに効く指定はここで持つ。
    // <style>側は非iOS（ブラウザのネイティブ印刷）用に同じ内容を残してある
    const tdS = 'padding:6px 8px 8px;border:1px solid #ddd;font-size:10px;vertical-align:top;word-break:break-word;line-height:1.5';
    const thS = 'background:#3f51b5;color:#fff;padding:6px 8px 8px;text-align:left;border:1px solid #ccc;font-size:11px;word-break:break-word;line-height:1.5;-webkit-print-color-adjust:exact;print-color-adjust:exact';
    const tableS = 'width:100%;border-collapse:collapse;margin-top:12px;table-layout:fixed';

    // 画像はPDF用に縮小してから埋め込む（downscaleForPdf のコメント参照）。
    // 元のitemsは変更しない（画面表示と再保存に使うため）
    const pdfItems = await Promise.all(itemsToExport.map(async (item) => ({
      ...item,
      images: await Promise.all((item.images || []).map((img) => downscaleForPdf(img, 300))),
    })));

    const rowsHtml = pdfItems.map((item, idx) => `
      <tr style="background:${idx % 2 === 0 ? '#fff' : '#f9f9f9'}">
        <td style="${tdS}">${formatSerial(item.serial, item.dbId ? '番号なし' : '未保存')}</td>
        <td style="${tdS}"><img src="${esc(item.images[0])}" style="width:60px;height:60px;object-fit:cover;border-radius:4px" /></td>
        <td style="${tdS}">${esc(item.lotNumber) || '―'}</td>
        <td style="${tdS}">${esc(item.label_product_name)}</td>
        <td style="${tdS}">${esc(item.detected_food_visual)}</td>
        <td style="${tdS};font-weight:bold;color:${item.match_status === 'DANGER' ? 'red' : 'green'}">${item.match_status === 'SAFE' ? '✓ SAFE' : '✗ DANGER'}${blurryImageBadgeHtml(item.image_quality)}</td>
        <td style="${tdS}">${item.label2_product_name != null ? `[ラベル②: ${esc(item.label2_product_name)} → ${item.label_consistency === 'MISMATCH' ? '<span style="color:red;font-weight:bold">ラベルNG</span>' : 'ラベルOK'}] ` : ''}${item.label_quantity != null ? `[個数: ラベル${esc(item.label_quantity)} / 実数${item.detected_quantity != null ? esc(item.detected_quantity) : '?'}個 → ${item.quantity_status === 'MISMATCH' ? '<span style="color:orange;font-weight:bold">個数NG</span>' : item.quantity_status === 'UNCERTAIN' ? '要目視' : '個数OK'}] ` : ''}${esc(item.reason)}${item.ingredients_check ? ' ⚠️ ' + esc(item.ingredients_check) : ''}</td>
      </tr>`).join('');

    const html = `<!DOCTYPE html>
<html lang="ja"><head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width,initial-scale=1">
  <title>${esc(filename)}</title>
  <style>
    body{font-family:sans-serif;padding:16px;font-size:11px}
    h2{font-size:16px;margin:0 0 8px}
    p{margin:2px 0}
    table{width:100%;border-collapse:collapse;margin-top:12px;table-layout:fixed}
    th{background:#3f51b5;color:#fff;padding:6px 8px 8px;text-align:left;border:1px solid #ccc;font-size:11px;word-break:break-word;line-height:1.5}
    @media print{@page{margin:10mm}html{-webkit-print-color-adjust:exact;print-color-adjust:exact}}
  </style>
</head><body>
  <div style="font-family:sans-serif;font-size:11px">
  <h2 style="font-size:16px;margin:0 0 8px">ProductMatcher 照合レポート</h2>
  <p style="margin:2px 0">店舗: ${esc(storeName) || '―'}　/　部門: ${esc(deptLabel(deptName)) || '―'}</p>
  <p style="margin:2px 0">出力者: ${esc(profile?.email || session?.user?.email || '―')}　/　出力日時: ${dateStr}　/　検査モード: ${speedMode ? '⚡速度優先' : '🔍精度優先'}</p>
  <p style="margin:2px 0">合計: ${itemsToExport.length}件　SAFE: ${safeCount}件　<span style="color:red">DANGER: ${dangerCount}件</span></p>
  <table style="${tableS}">
    <colgroup>
      <col style="width:9%"><col style="width:11%"><col style="width:8%"><col style="width:13%"><col style="width:13%"><col style="width:10%"><col style="width:36%">
    </colgroup>
    <thead><tr>
      <th style="${thS}">検査番号</th><th style="${thS}">画像</th><th style="${thS}">ロット</th><th style="${thS}">ラベル商品名</th><th style="${thS}">AIの見た中身</th><th style="${thS}">判定</th><th style="${thS}">理由</th>
    </tr></thead>
    <tbody>${rowsHtml}</tbody>
  </table>
  </div>
  ${AUTO_PRINT_SCRIPT}
</body></html>`;

    const isIOS = isIOSDevice();

    if (isIOS) {
      // navigator.share はタップ直後の一時的なユーザーアクティベーション（約5秒）を
      // 要求する。生成がこの枠に収まればそのまま共有シートまで進む（shareOrDefer）。
      // 失効した場合は NotAllowedError（AbortErrorではない）で拒否され、ダウンロードに
      // フォールバックしてもiOS Safariはblob URLのdownload属性を無視するため、PDFが
      // ビューアで表示されるだけになる。そのため生成済みPDFを保持しておき、
      // 「PDF出力 & クリア」を押し直したときに共有だけをやり直す（exportAndClear冒頭）
      const printHtml = html.replace(/<script>[\s\S]*?<\/script>/g, '');
      const worker = html2pdf().set({
        margin: 10,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: { scale: 2, useCORS: true },
        jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' }
      }).from(printHtml, 'string');
      const blob = await worker.outputPdf('blob');
      // 生成がユーザーアクティベーションの枠（約5秒）に収まっていれば、そのまま
      // 共有シートまで進む。間に合わなかった場合だけ「PDFを保存」ボタンに退避する。
      // 照合結果のクリアは保存まで到達したときだけ（キャンセルで消えないように）
      const saved = await shareOrDefer(blob, filename);
      if (saved) setItems([]);
    } else {
      const w = window.open('', '_blank');
      if (!w) { alert('ポップアップがブロックされました。ブラウザの設定を確認してください。'); return; }
      w.document.write(html);
      w.document.close();
      setItems([]);
    }
  };

  // 通知の帯の［再読み込み］。画面に検査結果が残っている場合は、未保存・未出力の結果が
  // 誤って消えないよう、1回目で注意を出し、もう一度押したときだけ読み込み直す
  // （このアプリは window.confirm を使わない方針。tests/pdfSaveReliability.test.mjs）
  const [reloadArmed, setReloadArmed] = useState(false);
  // 読み込み直すと消えるもの（検査結果・撮影中の写真・解析や保存・出力の途中）が画面に
  // あるか。検査結果だけを見ると、最初の解析の途中や写真を撮り溜めている間に1回で
  // 読み込み直してしまい、撮った写真や解析中の結果が消える
  const hasWorkOnScreen = items.length > 0 || currentSessionImages.length > 0 || batchImages.length > 0
    || analyzing || exporting || savingBeforeExport || !!batchProgress;
  // 作業が片づいたら注意の状態を戻す（残したままだと、次に結果が並んだときに1回目の
  // タップで注意なしに読み込み直してしまう）
  useEffect(() => { if (!hasWorkOnScreen) setReloadArmed(false); }, [hasWorkOnScreen]);
  const reloadForUpdate = () => {
    if (hasWorkOnScreen && !reloadArmed) { setReloadArmed(true); return; }
    window.location.reload();
  };

  // ヘッダーのメタ情報（バージョン・バッジ・会社名・店舗）とアクションボタン群。
  // 画面幅で2種類のレイアウトを出し分けるため、内容は変数に切り出して共有する
  // （ロジックの二重管理を避けるため。表示されるのは常にどちらか一方だけ）
  const headerMeta = (
    <>
      <div className="flex items-center gap-1.5 flex-wrap mt-0.5">
        <span className="text-xs font-normal text-indigo-300">{APP_VERSION}</span>
        <span className="text-[10px] font-bold bg-amber-400 text-amber-900 px-1.5 py-0.5 rounded">試験運用中</span>
        {company && !company.is_active && <span className="text-xs bg-red-600 text-white px-2 py-0.5 rounded">契約終了</span>}
        {(profile?.role === 'superadmin' || (company?.contracted_apps || []).includes('labelguard')) && (
          <a href="https://app.ok-retail.com/lg/" className="text-[10px] font-bold bg-white/20 hover:bg-white/35 text-white px-2 py-0.5 rounded-full no-underline shadow-sm">表示確認画面へ</a>
        )}
      </div>
      {(company?.name || profile?.name || session?.user?.email) && (
        <div className="text-xs text-indigo-300 leading-tight mt-0.5 truncate">
          {company?.name && <span className="font-medium">{company.name}</span>}
          {company?.name && (profile?.name || session?.user?.email) && <span className="mx-1">·</span>}
          {(profile?.name || session?.user?.email) && <span>{profile?.role === 'superadmin' ? 'SuperAdmin' : profile?.name || session.user.email}</span>}
        </div>
      )}
      <div className="text-xs text-indigo-200 leading-tight truncate">
        🏪 {storeName || '未設定'}{deptName && <span> · {deptLabel(deptName)}</span>}
      </div>
    </>
  );
  // 「PDF出力 & クリア」はアイコン群と分けて持つ。400px以上のレイアウトでは、これを
  // アイコン行ではなくメタ情報の行の右端（ヘルプアイコンの下）に置くことで、アイコン群が
  // ロゴと同じ行に収まるようにしている
  // 生成済みPDFが残っている場合（アクティベーション切れで共有シートを開けなかった
  // 場合）も同じボタンのまま。押し直すと exportAndClear が共有だけを行う
  const headerPdfButton = (
    items.length > 0 && (
      <button
        onClick={exportAndClear}
        disabled={exporting}
        className="text-xs bg-green-600 hover:bg-green-500 disabled:opacity-50 px-2 py-1 rounded flex items-center gap-1 whitespace-nowrap min-[400px]:shrink-0"
      >
        <FileDown className="w-4 h-4" /> {exporting ? (savingBeforeExport ? '保存を確認中...' : '生成中...') : `PDF出力 & クリア (${items.length})`}
      </button>
    )
  );
  const headerIconButtons = (
    <>
      {profile?.role === 'superadmin' && (
        <button onClick={() => setShowAdminPanel('superadmin')} className="text-xs bg-slate-600 px-2 min-[400px]:px-1.5 sm:px-2 py-1 rounded flex items-center gap-1">
          <ShieldCheck className="w-4 h-4" />
        </button>
      )}
      {(profile?.role === 'admin' || profile?.role === 'superadmin') && (
        <button onClick={() => setShowAdminPanel('admin')} className="text-xs bg-indigo-800 px-2 min-[400px]:px-1.5 sm:px-2 py-1 rounded flex items-center gap-1">
          <Users className="w-4 h-4" />
        </button>
      )}
      <button onClick={openHistoryTab} title="検査履歴" className="text-xs bg-indigo-800 px-2 min-[400px]:px-1.5 sm:px-2 py-1 rounded flex items-center">
        <History className="w-4 h-4" />
      </button>
      <button onClick={() => setShowSettings(!showSettings)} title="設定" className="text-xs bg-indigo-800 px-2 min-[400px]:px-1.5 sm:px-2 py-1 rounded flex items-center">
        <Settings className="w-4 h-4" />
      </button>
      <button onClick={openHelp} className="text-xs bg-indigo-800 px-2 min-[400px]:px-1.5 sm:px-2 py-1 rounded flex items-center gap-1">
        <HelpCircle className="w-4 h-4" />
      </button>
    </>
  );

  return (
    <>
    {showAdminPanel === 'admin' && <CompanyAdminPanel onClose={() => setShowAdminPanel(false)} onSignOut={signOut} />}
    {showAdminPanel === 'superadmin' && <SuperAdminPanel onClose={() => setShowAdminPanel(false)} onSignOut={signOut} />}
    <div className="app-root min-h-screen bg-slate-50 text-slate-800 font-sans pb-32 overflow-x-hidden">
      <header className="bg-indigo-700 text-white p-4 shadow-md sticky top-0 z-10">
        {/* 新しいバージョンの通知。ヘッダー（スクロールしても上に残る）の1行目に置き、
            ヘッダーのボタン類を隠さない。管理パネル等の全画面表示中はその下に隠れ、
            閉じると見える */}
        {updateAvailable && (
          <div role="status" className="max-w-md mx-auto mb-3 flex items-center gap-2 bg-amber-400 text-amber-950 rounded px-3 py-2 text-sm">
            <RefreshCw className="w-4 h-4 shrink-0" />
            <span className="flex-1 font-medium leading-snug">
              {reloadArmed && hasWorkOnScreen
                ? '再読み込みすると画面の検査結果や撮影中の写真は消えます。保存・PDF出力が済んでいれば、もう一度押してください'
                : '新しいバージョンがあります。作業の区切りで再読み込みしてください'}
            </span>
            <button onClick={reloadForUpdate} className="shrink-0 bg-amber-900 text-white font-bold px-3 py-1 rounded hover:bg-amber-800">
              {reloadArmed && hasWorkOnScreen ? 'もう一度押す' : '再読み込み'}
            </button>
          </div>
        )}
        {/* 400px未満（iPhone SE3等）: 従来のレイアウトをそのまま使う。ロゴが原寸で
            収まらない場合はflex-wrapでアイコン群が次の行へ折り返す */}
        <div className="min-[400px]:hidden flex flex-wrap justify-between items-center max-w-md mx-auto gap-2">
          <div className="min-w-0 shrink">
            <h1 className="text-xl font-bold flex items-center gap-2 whitespace-nowrap">
              <Eye className="w-6 h-6 shrink-0" />
              <img src="wordmark-white.png" alt="ProductMatcher" width={378} height={51} className="h-6 w-auto max-w-none shrink-0" />
            </h1>
            {headerMeta}
          </div>
          <div className="flex items-center gap-2 shrink-0 ml-auto">
            {headerPdfButton}{headerIconButtons}
          </div>
        </div>
        {/* 400px以上（Android 412px等）: ロゴとアイコン群を1行目に並べ、メタ情報を全幅の
            下段へ回す。左カラムの最も広い行（会社名・店舗）が折り返しの引き金になっていた
            ため、メタ情報を折り返し判定から外すのが要点。ロゴ横のアイコンは幅に余裕のある
            sm以上でのみ表示する */}
        <div className="hidden min-[400px]:flex flex-wrap items-center max-w-md mx-auto gap-x-2 gap-y-1">
          <h1 className="text-xl font-bold flex items-center gap-2 whitespace-nowrap shrink-0">
            <Eye className="w-6 h-6 shrink-0 hidden sm:block" />
            <img src="wordmark-white.png" alt="ProductMatcher" width={378} height={51} className="h-6 w-auto max-w-none shrink-0" />
          </h1>
          <div className="flex items-center gap-1 sm:gap-2 shrink-0 ml-auto">
            {headerIconButtons}
          </div>
          <div className="w-full flex items-start gap-2">
            <div className="min-w-0 flex-1">
              {headerMeta}
            </div>
            {headerPdfButton}
          </div>
        </div>
      </header>

      {showHelp && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-4">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white rounded-t-lg">
              <h2 className="text-lg font-bold text-indigo-700 flex items-center gap-2"><HelpCircle className="w-5 h-5" /> ヘルプ</h2>
              <button onClick={() => setShowHelp(false)} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            {/* superadmin・adminはデバッグ・サポート用に下位ロール向けヘルプも確認できる */}
            {helpTabsForRole(profile?.role).length > 0 && (
              <div className="px-4 py-2 border-b bg-slate-50 flex flex-wrap items-center gap-2 text-xs">
                <span className="text-slate-500">表示するヘルプ:</span>
                {helpTabsForRole(profile?.role).map(tab => (
                  <button
                    key={tab.key}
                    onClick={() => switchHelpRole(tab.key)}
                    className={`px-2 py-1 rounded border ${helpRole === tab.key ? 'bg-indigo-700 text-white border-indigo-700' : 'bg-white text-slate-600 border-slate-300 hover:bg-slate-100'}`}
                  >
                    {tab.label}
                  </button>
                ))}
              </div>
            )}
            <div ref={helpScrollRef} className="p-6 prose prose-sm max-w-none overflow-y-auto" style={{maxHeight: '75vh'}}>
              {helpError && <p className="text-sm text-red-600">{helpError}</p>}
              {/* 取得済みのロールはすべてマウントしたままにし、非表示側はhiddenで隠す。
                  タブを切り替えるたびにMarkdownを再パースしないための措置 */}
              {helpBodyReady && HELP_ROLE_TABS.map(tab => (
                helpContent[tab.key] !== undefined && (
                  <div key={tab.key} hidden={tab.key !== helpRole}>
                    <HelpMarkdown content={helpContent[tab.key]} />
                  </div>
                )
              ))}
              {/* 本文が未取得なら（先読み中・クリック後の取得中いずれも）読み込み中を出す */}
              {!helpError && helpContent[helpRole] === undefined && (
                <p className="text-sm text-slate-500 flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 animate-spin" /> 読み込み中…
                </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* 検査履歴モーダル（一般ユーザー向け、自分自身の検査のみ。絞り込み範囲は/api/history側で
          常にuser_emailに限定される） */}
      {showHistory && (
        <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-start justify-center p-4 overflow-y-auto">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl my-4">
            <div className="flex justify-between items-center p-4 border-b sticky top-0 bg-white rounded-t-lg z-10">
              <h2 className="text-lg font-bold text-indigo-700 flex items-center gap-2"><History className="w-5 h-5" /> 検査履歴（自分の検査のみ）</h2>
              <button onClick={() => setShowHistory(false)} className="text-slate-400 hover:text-slate-600"><X className="w-6 h-6" /></button>
            </div>
            <div className="p-4 border-b bg-slate-50 space-y-2">
              <DateRangePicker
                from={histDateFrom}
                to={histDateTo}
                onChange={({ from, to }) => { setHistDateFrom(from); setHistDateTo(to); }}
              />
              <div>
                <label className="text-[10px] text-slate-400">商品名（部分一致）</label>
                <input type="text" value={histProductName} onChange={e => setHistProductName(e.target.value)} placeholder="例: 唐揚げ弁当" className="w-full text-xs border rounded px-1.5 py-1" />
              </div>
              <div className="flex gap-2 flex-wrap items-center">
                <span className="text-xs text-slate-500">判定:</span>
                {['SAFE', 'DANGER', 'ERROR'].map(level => (
                  <label key={level} className="flex items-center gap-1 text-sm cursor-pointer">
                    <input type="checkbox" checked={histStatus.includes(level)} onChange={e => setHistStatus(prev => e.target.checked ? [...prev, level] : prev.filter(v => v !== level))} className="rounded" />
                    <span className={`text-xs font-bold px-1.5 py-0.5 rounded ${
                      level === 'DANGER' ? 'bg-red-100 text-red-700' :
                      level === 'ERROR' ? 'bg-slate-100 text-slate-500' :
                      'bg-blue-100 text-blue-700'
                    }`}>{level}</span>
                  </label>
                ))}
              </div>
              <div className="flex gap-2">
                <button onClick={fetchHistory} disabled={historyLoading} className="flex-1 text-xs bg-indigo-600 hover:bg-indigo-500 disabled:bg-slate-300 text-white font-bold py-1.5 rounded flex items-center justify-center gap-1">
                  <RefreshCw className={`w-3.5 h-3.5 ${historyLoading ? 'animate-spin' : ''}`} /> 検索
                </button>
                <button onClick={handleHistoryBulkPdf} disabled={historyLoading || historyPdfBusy || historyItems.length === 0} className="text-xs bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 font-bold py-1.5 px-3 rounded flex items-center gap-1">
                  <FileText className="w-3.5 h-3.5" /> {historyPdfBusy ? '生成中...' : `全件PDF（${historyItems.length}件）`}
                </button>
                <button onClick={exportHistoryCSV} disabled={historyLoading || historyItems.length === 0} className="text-xs bg-green-50 text-green-700 hover:bg-green-100 disabled:opacity-50 font-bold py-1.5 px-3 rounded flex items-center gap-1">
                  <Download className="w-3.5 h-3.5" /> CSV（{historyItems.length}件）
                </button>
              </div>
              {/* 生成に時間がかかりユーザーアクティベーションが切れたときだけ出る。
                  navigator.shareはこのボタンのタップ自体を新しいアクティベーション
                  として使う（usePendingPdfShare参照） */}
              {historyPendingPdf && (
                <button onClick={shareHistoryPendingPdf} className="w-full text-xs bg-amber-500 hover:bg-amber-400 text-white font-bold py-1.5 rounded flex items-center justify-center gap-1">
                  <FileText className="w-3.5 h-3.5" /> PDFを保存
                </button>
              )}
            </div>
            <div className="p-4 overflow-y-auto" style={{maxHeight: '65vh'}}>
              {historyLoading ? (
                <div className="text-center text-sm text-slate-400 py-8">読み込み中…</div>
              ) : historyItems.length === 0 ? (
                <div className="text-center text-sm text-slate-400 py-8">該当する検査履歴がありません</div>
              ) : (
                historyItems.map(insp => {
                  const fb = getLatestFeedback(insp.inspection_feedbacks);
                  return (
                    <div key={insp.id} className="mb-3 relative">
                      <button
                        onClick={() => handleHistoryItemPdf(insp)}
                        disabled={historyPdfBusy}
                        className="absolute top-3 right-3 text-[10px] px-2 py-0.5 rounded bg-indigo-50 text-indigo-700 hover:bg-indigo-100 disabled:opacity-50 flex items-center gap-1 z-10"
                      >
                        <FileText className="w-3 h-3" /> PDF
                      </button>
                      <div dangerouslySetInnerHTML={{ __html: buildInspectionCardHtml(insp) }} />
                      {fb && (
                        <div className="text-[10px] text-slate-400 mt-1 px-1">
                          フィードバック: {fb.verdict === 'correct' ? '✓ 正しい' : fb.verdict === 'incorrect' ? '✗ 誤り' : '? 不明'}
                          {fb.comment ? ` — ${fb.comment}` : ''}
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      <main className="max-w-md mx-auto p-4">
        {/* 免責事項（常時表示） */}
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 text-xs text-slate-500 leading-relaxed">
          <p className="font-bold mb-0.5">⚠️ 免責事項</p>
          <ul className="list-disc list-inside space-y-0.5">
            <li>本システムは、食品の照合確認作業を支援するものであり、品質管理上の最終判断を保証するものではありません。</li>
            <li>AIはハルシネーション（事実と異なる内容の生成）を起こす可能性があるため、最終的な確認および出荷の判断は、必ず品質管理責任者が行ってください。</li>
            <li>照合結果の正確性に関する最終的な判断および責任は、ご利用の事業者様が負うものとします。</li>
          </ul>
        </div>

        {showSettings && (
          <div className="bg-white p-4 rounded-lg shadow mb-4 border border-indigo-100 space-y-3">
            {/* ログイン情報 */}
            {session && (
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-bold text-slate-500">ログイン中</div>
                  <div className="text-sm text-slate-700">{profile?.name || session.user?.email || '（名前未設定）'}</div>
                  {session?.user?.email && <div className="text-xs text-slate-400">{session.user.email}</div>}
                  {company && <div className="text-xs text-slate-400">{company.name}</div>}
                </div>
                <button onClick={signOut} className="text-xs text-slate-400 hover:text-red-500">ログアウト</button>
              </div>
            )}

            {/* 店舗情報 */}
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">店舗名</label>
                <input
                  type="text"
                  value={storeName}
                  onChange={(e) => setStoreName(e.target.value)}
                  placeholder="例: ○○スーパー"
                  className="w-full p-2 border rounded bg-slate-50 text-sm"
                />
              </div>
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">部門名</label>
                <select value={deptName} onChange={(e) => setDeptName(e.target.value)} className="w-full p-2 border rounded bg-slate-50 text-sm">
                  {DEPARTMENT_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt === '' ? '（未設定）' : deptLabel(opt)}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* 検査モード切替 */}
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">検査モード</label>
              <div className="flex rounded overflow-hidden border border-slate-200 w-fit">
                <button
                  onClick={() => setSpeedMode(false)}
                  className={`text-xs px-3 py-1.5 transition-colors ${!speedMode ? 'bg-indigo-600 text-white font-bold' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                >
                  精度優先
                </button>
                <button
                  onClick={() => setSpeedMode(true)}
                  className={`text-xs px-3 py-1.5 transition-colors ${speedMode ? 'bg-amber-500 text-white font-bold' : 'bg-white text-slate-500 hover:bg-slate-50'}`}
                >
                  速度優先
                </button>
              </div>
              <p className="text-[10px] text-slate-400 mt-1">
                {speedMode ? '速度優先: 画像を圧縮して高速処理（精度やや低下）' : '精度優先: 高解像度で解析（標準）'}
              </p>
            </div>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input type="checkbox" checked={showAnalyzeTime} onChange={(e) => setShowAnalyzeTime(e.target.checked)} className="w-4 h-4 rounded border-slate-300" />
              <span className="text-slate-700">⏱ 解析時間表示</span>
              <span className="text-xs text-slate-400">（応答時間とモデル名）</span>
            </label>

            {/* モデル選択（SuperAdminのみ） */}
            {profile?.role === 'superadmin' && (
              <div>
                <div className="flex justify-between items-end mb-1">
                  <label className="block text-xs font-bold text-slate-500">使用モデル (Engine)</label>
                  <button
                    onClick={fetchModels}
                    disabled={loadingModels}
                    className="text-xs text-indigo-600 flex items-center gap-1 hover:underline disabled:text-slate-400"
                  >
                    <RefreshCw className={`w-3 h-3 ${loadingModels ? 'animate-spin' : ''}`} />
                    一覧取得
                  </button>
                </div>

                {availableModels.length > 0 ? (
                  <select
                    value={modelName}
                    onChange={(e) => setModelName(e.target.value)}
                    className="w-full p-2 border rounded bg-white text-sm"
                  >
                    {availableModels.map(m => <option key={m} value={m}>{m}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={modelName}
                    onChange={(e) => setModelName(e.target.value)}
                    placeholder="例: gemini-2.0-flash-lite"
                    className="w-full p-2 border rounded bg-slate-50 text-sm"
                  />
                )}
                <div className="flex justify-between items-center mt-1">
                  <p className="text-xs text-slate-400">※現在: <span className="font-mono font-bold text-slate-600">{modelName}</span> を使用中</p>
                  {modelName !== DEFAULT_MODEL && (
                    <button onClick={() => setModelName(DEFAULT_MODEL)} className="text-xs text-orange-600 hover:underline">デフォルトに戻す</button>
                  )}
                </div>
              </div>
            )}

            <p className="text-xs text-slate-400 mt-1">
              動作モード: {isStandalone ? '📱 ホーム画面から起動（アプリ版）' : '🌐 ブラウザで表示中'}
              {!isStandalone && <span className="text-slate-300">（共有メニューの「ホーム画面に追加」でアプリ化できます）</span>}
            </p>
            <p className="text-xs text-slate-400">端末: {DEVICE_TYPE} / {BROWSER_TYPE}</p>

            {/* デプロイ情報 */}
            {/* 表示するのは今動いている画面のビルドのコミット（新しい版が出た後に
                サーバー側のコミットを出すと、古い画面なのに新しい版に見えてしまうため） */}
            {(BUILD_COMMIT || deployInfo?.commitShort) && (
              <p className="text-xs text-slate-400 mt-1">デプロイ: {APP_VERSION} #{BUILD_COMMIT ? BUILD_COMMIT.slice(0, 7) : deployInfo.commitShort}{deployInfo?.env ? `（${deployInfo.env}）` : ''}</p>
            )}
            {!(BUILD_COMMIT || deployInfo?.commitShort) && (
              <div className="text-right">
                <span className="text-xs text-slate-400 font-mono">{APP_VERSION}</span>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3">
          <h2 className="text-sm font-bold text-slate-500 mb-2">照合履歴</h2>
          {/* 未保存の件数をまとめて知らせるバナー。個々のカードにもバッジは出るが、
              件数が増えるとスクロールしないと気付けないため先頭に出す
              （姉妹アプリfood-label-assistと同じ形。PR#138参照） */}
          {unsavedCount > 0 && (
            <div className="px-3 py-2 rounded-lg border border-amber-200 bg-amber-50 text-xs text-amber-800">
              ⚠ {unsavedCount}件が保存されていません。各カードの「再保存」で送り直せます。PDF出力でクリアすると復旧できません。
            </div>
          )}
          {items.length === 0 ? (
            <div className="text-center py-10 text-slate-400 border border-dashed rounded-lg">
              <Eye className="w-12 h-12 mx-auto mb-2 opacity-30" />
              <p>中身とラベルを撮影して<br/>貼り間違いをチェック</p>
            </div>
          ) : (
            items.map((item) => (
              <div key={item.id} className="bg-white p-4 rounded-lg shadow-sm border border-slate-100">
                <div className="flex gap-4 mb-2">
                  <div className="w-16 h-16 bg-slate-100 rounded overflow-hidden flex-shrink-0 relative">
                    <img src={item.images[0]} className="w-full h-full object-cover" alt="product" />
                    {item.images.length > 1 && (
                      <div className="absolute top-0 right-0 bg-indigo-600 text-white text-[10px] px-1 font-bold">+{item.images.length - 1}</div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className={`text-xs font-bold px-2 py-1 rounded inline-block mb-1 ${getRiskColor(item.match_status)}`}>
                      {item.match_status === 'SAFE' ? '一致 (OK)' : '不一致 (NG)'}
                    </div>
                    {isBlurryImage(item.image_quality) && (
                      <span className="ml-1 inline-flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded bg-amber-500 text-white align-top">
                        <ImageOff className="w-3 h-3" aria-hidden="true" />{BLURRY_IMAGE_LABEL}
                      </span>
                    )}
                    <div className="flex items-center gap-2 flex-wrap">
                      {item.serial && (
                        <span className="text-xs font-bold bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded font-mono">{formatSerial(item.serial)}</span>
                      )}
                      {/* 保存できなかった項目は検査番号が付かない。以前はバッジを出さないだけで
                          画面上は保存済みと見分けがつかず、PDFに出力して初めて気付く状態だった
                          （姉妹アプリfood-label-assistで見つかった不具合と同じ構造。PR#138参照）。
                          未保存であることと再保存の手段をここで示す */}
                      {item.saving && (
                        <span className="text-xs bg-slate-100 text-slate-500 px-2 py-0.5 rounded">保存中...</span>
                      )}
                      {!item.saving && item.saveError && (
                        <span className="inline-flex items-center gap-1">
                          <span
                            className="text-xs bg-amber-50 text-amber-700 border border-amber-200 px-2 py-0.5 rounded font-bold"
                            title={item.saveError}
                          >
                            ⚠ {item.dbId ? '番号なし' : '未保存'}
                          </span>
                          {!item.dbId && (
                            <button
                              onClick={() => retrySave(item)}
                              className="text-xs bg-amber-600 hover:bg-amber-500 text-white px-2 py-0.5 rounded font-bold"
                            >
                              再保存
                            </button>
                          )}
                        </span>
                      )}
                      {item.lotNumber && (
                        <span className="text-xs text-slate-400 font-mono">Lot: {item.lotNumber}</span>
                      )}
                    </div>
                    <div className="text-sm font-bold text-slate-700 truncate">ラ①: {item.label_product_name}</div>
                    {item.label2_product_name != null && (
                      <div className="text-sm text-slate-600 truncate">ラ②: {item.label2_product_name}</div>
                    )}
                    <div className="text-xs text-slate-500 truncate">目: {item.detected_food_visual}</div>
                    {isBlurryImage(item.image_quality) && (
                      <p className="mt-1 flex items-start gap-1 text-xs font-bold text-amber-800 bg-amber-50 border border-amber-300 rounded px-2 py-1">
                        <Camera className="w-3.5 h-3.5 shrink-0 mt-px" aria-hidden="true" />{BLURRY_RETAKE_MESSAGE}
                      </p>
                    )}
                  </div>
                </div>

                {item.match_status === 'DANGER' && (
                  <div className="bg-red-50 p-2 rounded text-sm text-red-700 mb-2 border border-red-100 font-bold">
                    <AlertTriangle className="w-3 h-3 inline mr-1"/>
                    {item.reason}
                  </div>
                )}

                {item.quantity_status === 'MISMATCH' && item.match_status !== 'DANGER' && (
                  <div className="bg-orange-50 p-2 rounded text-sm text-orange-700 mb-2 border border-orange-100 font-bold">
                    <AlertTriangle className="w-3 h-3 inline mr-1"/>
                    個数不一致: ラベル {item.label_quantity} / 実数 {item.detected_quantity}個
                  </div>
                )}

                {item.quantity_status === 'UNCERTAIN' && (
                  <div className="text-xs text-yellow-600 mb-1">
                    🔢 個数: {item.label_quantity} ／ 目視確認推奨（袋・重なりで自動計数不可）
                  </div>
                )}

                {item.label_quantity != null && item.quantity_status === 'MATCH' && (
                  <div className="text-xs text-slate-400 mb-1">
                    🔢 個数: {item.label_quantity} = {item.detected_quantity}個 ✓
                  </div>
                )}

                <div className="flex justify-between items-center mt-2">
                  {item.analyzeMs && showAnalyzeTime && (
                    <span className="text-[10px] text-slate-300 font-mono">⏱ {(item.analyzeMs / 1000).toFixed(2)}s · {item.modelUsed}</span>
                  )}
                  <button
                    onClick={() => setItems(prev => prev.filter(i => i.id !== item.id))}
                    className="text-slate-300 hover:text-red-500 text-xs flex items-center gap-1 ml-auto"
                  >
                    <Trash2 className="w-3 h-3" /> 削除
                  </button>
                </div>

                {item.dbId && (
                  <div className="mt-2 pt-2 border-t border-slate-100">
                    {feedbacks[item.id] ? (
                      <span className="text-[10px] text-slate-400 bg-slate-100 px-2 py-0.5 rounded">
                        {feedbacks[item.id] === 'correct' ? '👍 正しい' : feedbacks[item.id] === 'incorrect' ? '👎 誤検知' : '❓ 不明'}
                      </span>
                    ) : feedbackDraft[item.id] ? (
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-slate-500">
                          {feedbackDraft[item.id].verdict === 'correct' ? '👍' : feedbackDraft[item.id].verdict === 'incorrect' ? '👎' : '❓'}
                        </span>
                        <input
                          type="text"
                          placeholder="コメント（任意）"
                          value={feedbackDraft[item.id].comment}
                          onChange={e => setFeedbackDraft(prev => ({ ...prev, [item.id]: { ...prev[item.id], comment: e.target.value } }))}
                          className="flex-1 text-xs border border-slate-200 rounded px-2 py-1 min-w-0"
                        />
                        <button
                          onClick={() => submitFeedback(item, feedbackDraft[item.id].verdict, feedbackDraft[item.id].comment)}
                          className="text-[10px] px-2 py-1 bg-indigo-500 text-white rounded hover:bg-indigo-600 whitespace-nowrap"
                        >送信</button>
                        <button
                          onClick={() => setFeedbackDraft(prev => { const n = { ...prev }; delete n[item.id]; return n; })}
                          className="text-[10px] px-1.5 py-1 text-slate-400 hover:text-slate-600"
                        >✕</button>
                      </div>
                    ) : (
                      <div className="flex items-center gap-1.5">
                        <span className="text-[10px] text-slate-400">判定FB:</span>
                        {[
                          { v: 'correct', label: '👍 正しい' },
                          { v: 'incorrect', label: '👎 誤検知' },
                          { v: 'uncertain', label: '❓ 不明' },
                        ].map(({ v, label }) => (
                          <button
                            key={v}
                            onClick={() => setFeedbackDraft(prev => ({ ...prev, [item.id]: { verdict: v, comment: '' } }))}
                            className="text-[10px] px-1.5 py-0.5 rounded border transition-colors border-slate-200 text-slate-400 hover:border-indigo-200 hover:text-indigo-500"
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))
          )}
        </div>
      </main>

      <div className="fixed bottom-6 left-0 right-0 flex justify-center items-center gap-6 z-20">
        <button onClick={() => galleryInputRef.current.click()} disabled={analyzing} className="w-14 h-14 rounded-full shadow-lg bg-white text-indigo-600 flex items-center justify-center">
          <ImageIcon className="w-6 h-6" />
        </button>
        <button onClick={() => cameraInputRef.current.click()} disabled={analyzing} className="rounded-full shadow-xl bg-indigo-600 text-white flex items-center justify-center" style={{width:'4.5rem', height:'4.5rem'}}>
          <Camera className="w-8 h-8" />
        </button>
        <button onClick={() => { setBatchImages([]); batchGalleryRef.current.value = ''; batchGalleryRef.current.click(); }} disabled={analyzing} title="バッチ処理" className="w-14 h-14 rounded-full shadow-lg bg-white text-indigo-600 flex items-center justify-center">
          <Layers className="w-6 h-6" />
        </button>
        <input type="file" accept="image/*" capture="environment" ref={cameraInputRef} className="hidden"
          onChange={(e) => { const isBatch = inputModeRef.current === 'batch'; inputModeRef.current = 'single'; isBatch ? handleBatchSelect(e) : handleImageSelect(e); }} />
        <input type="file" accept="image/*" ref={galleryInputRef} className="hidden" onChange={handleImageSelect} />
        <input type="file" accept="image/*" multiple ref={batchGalleryRef} className="hidden" onChange={handleBatchSelect} />
      </div>

      {/* 撮影セッションモーダル */}
      {showSessionModal && (
        <div className="fixed inset-0 bg-black/80 z-30 flex flex-col justify-end pb-8">
          <div className="p-4 text-white text-center">
            <h3 className="text-lg font-bold mb-2">撮影画像を確認 ({currentSessionImages.length}/2)</h3>
            <p className="text-sm opacity-80 mb-4">中身とラベルを別撮りする場合は追加できます</p>

            <div className="flex gap-2 justify-center mb-6 overflow-x-auto px-4">
              {currentSessionImages.map((img, idx) => (
                <div key={idx} className="w-20 h-20 rounded border-2 border-white overflow-hidden flex-shrink-0 relative">
                  <img src={img} className="w-full h-full object-cover" alt={`shot-${idx}`} />
                  <button
                    onClick={() => {
                      const next = currentSessionImages.filter((_, i) => i !== idx);
                      if (next.length === 0) { setShowSessionModal(false); setCurrentSessionImages([]); }
                      else { setCurrentSessionImages(next); }
                    }}
                    className="absolute top-0 right-0 bg-red-500 text-white p-0.5 rounded-bl"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              ))}

              {currentSessionImages.length < 2 && (
                <div className="flex flex-col gap-2">
                  <button
                    onClick={() => cameraInputRef.current.click()}
                    className="w-20 h-9 rounded border border-white/50 flex items-center justify-center text-white bg-indigo-600 hover:bg-indigo-500"
                  >
                    <Camera className="w-4 h-4 mr-1" />
                    <span className="text-[10px] font-bold">撮影</span>
                  </button>
                  <button
                    onClick={() => galleryInputRef.current.click()}
                    className="w-20 h-9 rounded border border-white/50 flex items-center justify-center text-indigo-600 bg-white hover:bg-slate-100"
                  >
                    <ImageIcon className="w-4 h-4 mr-1" />
                    <span className="text-[10px] font-bold">選択</span>
                  </button>
                </div>
              )}
            </div>

            <button
              onClick={() => analyzeImages(currentSessionImages)}
              disabled={analyzing || currentSessionImages.length === 0}
              className="w-full max-w-xs mx-auto py-3 bg-indigo-500 hover:bg-indigo-400 text-white font-bold rounded-full shadow-lg flex items-center justify-center gap-2"
            >
              {analyzing ? (
                <>
                  <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full"></div>
                  解析中...
                </>
              ) : (
                <>
                  <RefreshCw className="w-5 h-5" />
                  解析する
                </>
              )}
            </button>

            <button onClick={() => { setShowSessionModal(false); setCurrentSessionImages([]); }} className="mt-4 text-sm text-white/60 underline">
              キャンセル
            </button>
          </div>
        </div>
      )}

      {/* バッチ処理モーダル */}
      {showBatchModal && (
        <div className="fixed inset-0 bg-black/80 z-30 flex flex-col justify-end pb-8">
          <div className="p-4 text-white text-center">
            <h3 className="text-lg font-bold mb-1">バッチ解析 ({batchImages.length}件)</h3>
            <p className="text-sm opacity-70 mb-4">各画像を1商品として順番に解析します</p>

            <div className="flex gap-2 justify-start overflow-x-auto px-2 mb-6 pb-2">
              {batchImages.map((img, idx) => (
                <div key={idx} className="flex-shrink-0 relative">
                  <img src={img.base64} className="w-16 h-16 rounded object-cover border-2 border-white/40" alt={`batch-${idx}`} />
                  {batchProgress && batchResults[idx] && (
                    <div className={`absolute inset-0 rounded flex items-center justify-center ${batchResults[idx]?.match_status === 'DANGER' ? 'bg-red-500/70' : 'bg-green-500/70'}`}>
                      {batchResults[idx]?.match_status === 'DANGER'
                        ? <AlertTriangle className="w-6 h-6 text-white" />
                        : <CheckCircle className="w-6 h-6 text-white" />}
                    </div>
                  )}
                  {batchProgress && processingIndices.has(idx) && (
                    <div className="absolute inset-0 bg-indigo-500/70 rounded flex items-center justify-center">
                      <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
                    </div>
                  )}
                  {!batchProgress && (
                    <button onClick={() => setBatchImages(prev => prev.filter((_, i) => i !== idx))}
                      className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center">
                      <X className="w-3 h-3" />
                    </button>
                  )}
                </div>
              ))}
            </div>

            {batchProgress ? (
              <div className="text-center">
                <p className="text-lg font-bold mb-2">{batchProgress.current} / {batchProgress.total} 解析中...</p>
                <div className="w-full max-w-xs mx-auto bg-white/20 rounded-full h-2">
                  <div className="bg-indigo-400 h-2 rounded-full transition-all" style={{ width: `${(batchProgress.current / batchProgress.total) * 100}%` }} />
                </div>
                {batchAborting
                  ? <p className="mt-3 text-red-300 text-sm animate-pulse">現在の検査完了後に中止します...</p>
                  : <button onClick={() => { batchAbortRef.current = true; setBatchAborting(true); }} className="mt-3 px-4 py-2 bg-red-500 text-white rounded-lg text-sm">検査中止</button>
                }
              </div>
            ) : (
              <>
                <div className="flex gap-2 justify-center mb-3">
                  <button onClick={() => { inputModeRef.current = 'batch'; cameraInputRef.current.click(); }}
                    className="flex items-center gap-1 px-3 py-2 bg-white/20 text-white rounded-full text-sm font-bold">
                    <Camera className="w-4 h-4" /> 撮影で追加
                  </button>
                </div>
                <button onClick={processBatch} disabled={batchImages.length === 0}
                  className="w-full max-w-xs mx-auto py-3 bg-indigo-500 hover:bg-indigo-400 disabled:opacity-40 text-white font-bold rounded-full shadow-lg flex items-center justify-center gap-2">
                  <Layers className="w-5 h-5" />
                  {batchImages.length}件をまとめて解析
                </button>
                <button onClick={() => { setShowBatchModal(false); setBatchImages([]); }} className="mt-4 text-sm text-white/60 underline">
                  キャンセル
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {editingItem && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-md rounded-xl shadow-2xl flex flex-col max-h-[90vh]">
            <div className={`p-4 border-b flex justify-between items-center ${getRiskColor(editingItem.match_status)}`}>
              <h3 className="font-bold flex items-center gap-2 flex-wrap">
                {editingItem.match_status === 'SAFE' ? <CheckCircle className="w-5 h-5"/> : <AlertTriangle className="w-5 h-5"/>}
                判定: {editingItem.match_status}
                {isBlurryImage(editingItem.image_quality) && (
                  <span className="inline-flex items-center gap-1 text-xs font-bold px-2 py-0.5 rounded-full bg-amber-500 text-white">
                    <ImageOff className="w-3.5 h-3.5" aria-hidden="true" />{BLURRY_IMAGE_LABEL}
                  </span>
                )}
              </h3>
              <button onClick={() => setEditingItem(null)}><X className="w-6 h-6" /></button>
            </div>

            <div className="p-4 overflow-y-auto space-y-4">
              {/* PM は判定（SAFE/DANGER）に「画像の鮮明さ」の項目が無いため、理由（BLURRY_IMAGE_NOTE）も
                  ここで示す */}
              {isBlurryImage(editingItem.image_quality) && (
                <div className="flex items-start gap-2 text-sm text-amber-900 bg-amber-50 border border-amber-300 rounded-lg p-3" role="alert">
                  <Camera className="w-5 h-5 shrink-0 text-amber-600" aria-hidden="true" />
                  <span><span className="font-bold">{BLURRY_IMAGE_LABEL}: {BLURRY_RETAKE_MESSAGE}</span><br />{BLURRY_IMAGE_NOTE}</span>
                </div>
              )}
              <div className="flex gap-2 overflow-x-auto pb-2">
                {editingItem.images.map((img, i) => (
                  <img key={i} src={img} className="h-40 rounded object-contain bg-slate-100 border" alt="preview" />
                ))}
              </div>
              {editingItem.analyzeMs && showAnalyzeTime && (
                <div className="flex gap-2 text-xs text-slate-400 font-mono">
                  <span>⏱ {(editingItem.analyzeMs / 1000).toFixed(2)}s</span>
                  <span className="text-slate-300">|</span>
                  <span className="truncate">{editingItem.modelUsed}</span>
                </div>
              )}

              {/* ロット番号入力。以前は判定結果・ラベル間チェック・個数チェック・
                  AIコメントの後（モーダル最下部）にあり、条件付き表示のブロックが
                  重なるとスクロールしないと見つからなかった（姉妹アプリ
                  food-label-assistでは画像の直後・商品名の近くに置いている）。
                  常に見える位置まで引き上げる */}
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">ロット番号・商品番号（任意）</label>
                <input
                  type="text"
                  value={lotNumber}
                  onChange={(e) => setLotNumber(e.target.value)}
                  maxLength={50}
                  placeholder="例: L2026-001"
                  className="w-full p-2 border rounded bg-slate-50 text-sm"
                />
              </div>

              <div className="grid grid-cols-2 gap-2 bg-slate-50 p-3 rounded">
                <div>
                  <label className="block text-xs font-bold text-slate-500">📸 AIが見た中身</label>
                  <p className="font-bold text-indigo-800">{editingItem.detected_food_visual}</p>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500">🏷️ ラベル①</label>
                  <p className="font-bold text-slate-800">{editingItem.label_product_name}</p>
                  {editingItem.label2_product_name != null && (
                    <>
                      <label className="block text-xs font-bold text-slate-500 mt-1">🏷️ ラベル②</label>
                      <p className="font-bold text-slate-800">{editingItem.label2_product_name}</p>
                    </>
                  )}
                </div>
              </div>

              {editingItem.label2_product_name != null && (
                <div className={`p-3 rounded border ${editingItem.label_consistency === 'MISMATCH' ? 'bg-red-50 border-red-200' : 'bg-green-50 border-green-200'}`}>
                  <label className="block text-xs font-bold text-slate-500 mb-1">🏷️🏷️ ラベル間チェック</label>
                  <div className="flex items-center gap-2 text-sm flex-wrap">
                    <span className="text-slate-600">①<strong>{editingItem.label_product_name}</strong></span>
                    <span className="text-slate-400">vs</span>
                    <span className="text-slate-600">②<strong>{editingItem.label2_product_name}</strong></span>
                    <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded ${editingItem.label_consistency === 'MISMATCH' ? 'bg-red-200 text-red-800' : 'bg-green-200 text-green-800'}`}>
                      {editingItem.label_consistency === 'MISMATCH' ? 'ラベルNG' : 'ラベルOK'}
                    </span>
                  </div>
                </div>
              )}

              {editingItem.label_quantity != null && (
                <div className={`p-3 rounded border ${
                  editingItem.quantity_status === 'MISMATCH' ? 'bg-orange-50 border-orange-200' :
                  editingItem.quantity_status === 'UNCERTAIN' ? 'bg-yellow-50 border-yellow-200' :
                  'bg-green-50 border-green-200'
                }`}>
                  <label className="block text-xs font-bold text-slate-500 mb-1">🔢 個数チェック</label>
                  <div className="flex items-center gap-3 text-sm">
                    <span>ラベル: <strong>{editingItem.label_quantity}</strong></span>
                    <span className="text-slate-400">→</span>
                    <span>実数: <strong>{editingItem.detected_quantity != null ? `${editingItem.detected_quantity}個` : '確認不能'}</strong></span>
                    <span className={`ml-auto text-xs font-bold px-2 py-0.5 rounded ${
                      editingItem.quantity_status === 'MISMATCH' ? 'bg-orange-200 text-orange-800' :
                      editingItem.quantity_status === 'UNCERTAIN' ? 'bg-yellow-200 text-yellow-800' :
                      'bg-green-200 text-green-800'
                    }`}>
                      {editingItem.quantity_status === 'MISMATCH' ? '個数NG' :
                       editingItem.quantity_status === 'UNCERTAIN' ? '要目視確認' : '個数OK'}
                    </span>
                  </div>
                </div>
              )}

              <div className="bg-slate-50 p-3 rounded border border-slate-200">
                <label className="block text-xs font-bold text-slate-500 mb-1">AIコメント</label>
                <p className="text-sm font-bold text-slate-800">{editingItem.reason}</p>
                {editingItem.ingredients_check && (
                  <p className="text-sm text-red-600 mt-1">⚠️ {editingItem.ingredients_check}</p>
                )}
              </div>

            </div>

            <div className="p-4 border-t">
              <button onClick={() => saveItem(editingItem)} className="w-full py-3 bg-indigo-600 text-white font-bold rounded shadow">OK (確認)</button>
            </div>
          </div>
        </div>
      )}
    </div>

    {/* タイミングデバッグパネル（superadminのみ） */}
    {debugTiming && showAnalyzeTime && (
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-slate-900 text-white text-xs p-3 shadow-lg">
        <div className="flex justify-between items-center mb-2">
          <span className="font-bold text-slate-300">⏱ Inspection Timing</span>
          <button onClick={() => setDebugTiming(null)} className="text-slate-400 text-base leading-none px-2">✕</button>
        </div>
        {debugTiming.items.map((t, i) => (
          <div key={i} className={`font-mono ${debugTiming.items.length > 1 ? 'mb-2 border-b border-slate-700 pb-2' : ''}`}>
            {debugTiming.items.length > 1 && <div className="text-slate-400 mb-1">batch[{i}]</div>}
            <div className="grid grid-cols-2 gap-x-4 gap-y-0.5">
              {t.compress_ms != null && <><span className="text-slate-400">圧縮</span><span>{t.compress_ms} ms</span></>}
              {t.analyze_ms != null && <><span className="text-slate-400">AI判定</span><span>{t.analyze_ms} ms</span></>}
              {t.upload_ms != null && <><span className="text-slate-400">アップロード</span><span>{t.upload_ms} ms</span></>}
              {t.save_ms != null && <><span className="text-slate-400">DB保存</span><span>{t.save_ms} ms</span></>}
            </div>
          </div>
        ))}
      </div>
    )}
    </>
  );
}
